import DatePicker from "legacy/DatePicker"
import {
  centsToDollarsRaw,
  dollarsToCents,
  feetToInches,
  humanize,
  inchesToFeet,
} from "legacy/helpers.es6.jsx"
import AdvancedOptions from "legacy/pricing/AdvancedOptions"
import PricingExceptions from "legacy/pricing/PricingExceptions"
import WeekendDays from "legacy/pricing/WeekendDays"
import moment from "moment"
import PropTypes from "prop-types"
import React from "react"

class SpaceRateForm extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      weekendDays: {
        friday: true,
        saturday: true,
      },
      showingAdvancedOptions: false,
      showingExceptions: false,

      // form attributes
      spaceType: "Dock",
      timeframe: "nightly",
      rateType: "flat",
      startDate: "",
      endDate: "",
      minLength: "",
      maxLength: "",
      weekdayRate: "",
      weekendRate: "",
      minNights: "",
      minPrice: "",
      minBeam: 0,
      maxBeam: 1000,
      exceptions: [],
    }

    this.toggleWeekendDay = this.toggleWeekendDay.bind(this)
    this.createException = this.createException.bind(this)
    this.removeExceptionByIndex = this.removeExceptionByIndex.bind(this)
    this.currencySymbol = window.currentMarina?.currencySymbol || "$"
  }

  componentDidMount() {
    if (
      this.props.selectedSpaceRate &&
      Object.keys(this.props.selectedSpaceRate || {}).length > 0
    ) {
      this.useSpaceRate(this.props.selectedSpaceRate)
    }
  }

  useSpaceRate(spaceRate) {
    const weekdayRate = spaceRate.rates.monday
    const weekendDay = spaceRate.weekend_days[0] || "saturday"
    const weekendRate = spaceRate.rates[weekendDay]

    // Find all days that have the weekend rate
    const weekendDaysList = Object.keys(spaceRate.rates || {}).filter(
      (key) => spaceRate.rates[key] === weekendRate
    )

    // Create an object with those days as keys and true as values
    const weekendDays = weekendDaysList.reduce((obj, day) => {
      obj[day] = true
      return obj
    }, {})

    this.setState({
      id: spaceRate.id,
      timeframe: spaceRate.timeframe,
      spaceType: spaceRate.space_type,
      rateType: spaceRate.rate_type,
      startDate: moment.utc(spaceRate.start_date).format("L"),
      endDate: moment.utc(spaceRate.end_date).format("L"),
      minLength: inchesToFeet(spaceRate.min_length),
      maxLength: inchesToFeet(spaceRate.max_length),
      weekdayRate: centsToDollarsRaw(weekdayRate),
      weekendRate: centsToDollarsRaw(weekendRate),
      minNights: spaceRate.min_nights,
      minPrice: centsToDollarsRaw(spaceRate.min_price),
      minBeam: inchesToFeet(spaceRate.min_beam),
      maxBeam: inchesToFeet(spaceRate.max_beam),
      weekendDays,
      exceptions: (spaceRate.space_rate_exceptions || []).map((e) => {
        return {
          id: e.id,
          rate: centsToDollarsRaw(e.rate),
          startDate: moment.utc(e.start_date).format("L"),
          endDate: moment.utc(e.end_date).format("L"),
        }
      }),
    })
  }

  toggleWeekendDay(day) {
    const weekendDays = this.state.weekendDays
    if (Object.hasOwn(weekendDays, day)) {
      delete weekendDays[day]
    } else {
      weekendDays[day] = true
    }
    this.setState({ weekendDays })
  }

  toggleAdvancedOptions() {
    this.setState({
      showingAdvancedOptions: !this.state.showingAdvancedOptions,
    })
  }

  toggleExceptions() {
    this.setState({ showingExceptions: !this.state.showingExceptions })
  }

  timeframeSelect() {
    const options = []
    ;[
      "temporary",
      "nightly",
      "weekly",
      "monthly",
      "biannualy",
      "annualy",
    ].forEach((timeframe) => {
      options.push(
        <option key={timeframe} value={timeframe}>
          {humanize(timeframe)}
        </option>
      )
    })
    return (
      <select
        value={this.state.timeframe}
        className="form-control"
        onChange={(e) => this.setState({ timeframe: e.target.value })}
      >
        {options}
      </select>
    )
  }

  spaceTypeSelect() {
    const options = []
    ;["Dock", "Mooring"].forEach((rate) => {
      options.push(
        <option key={rate} value={rate}>
          {humanize(rate)}
        </option>
      )
    })
    return (
      <select
        value={this.state.spaceType}
        className="form-control"
        onChange={(e) => this.setState({ spaceType: e.target.value })}
      >
        {options}
      </select>
    )
  }

  rateTypeSelect() {
    const options = []
    ;["flat", "by_length"].forEach((rate) => {
      options.push(
        <option key={rate} value={rate}>
          {humanize(rate)}
        </option>
      )
    })
    return (
      <select
        value={this.state.rateType}
        className="form-control"
        onChange={(e) => this.setState({ rateType: e.target.value })}
      >
        {options}
      </select>
    )
  }

  createException(startDate, endDate, rate) {
    const exceptions = this.state.exceptions
    exceptions.push({ startDate, endDate, rate })
    this.setState({ exceptions })
  }

  removeExceptionByIndex(index) {
    const exceptions = this.state.exceptions
    if (index !== -1) {
      exceptions[index]._destroy = true
    }
    this.setState({ exceptions })
  }

  timeframeRateLangauge() {
    return this.state.timeframe === "temporary" ? "temporary" : "per night"
  }

  ifLongTimeframe() {
    // a long timeframe is something with weekly or above pricing
    return (
      this.state.timeframe !== "temporary" && this.state.timeframe !== "nightly"
    )
  }

  serialize() {
    const weekdayRate = dollarsToCents(this.state.weekdayRate)
    let weekendRate = dollarsToCents(this.state.weekendRate)
    if (this.ifLongTimeframe()) {
      weekendRate = weekdayRate
    }
    return {
      timeframe: this.state.timeframe,
      rate_type: this.state.rateType,
      space_type: this.state.spaceType,
      start_date: moment(this.state.startDate, "MM-DD-YYYY").toISOString(),
      end_date: moment(this.state.endDate, "MM-DD-YYYY").toISOString(),
      min_length: feetToInches(this.state.minLength),
      max_length: feetToInches(this.state.maxLength),
      min_price: Math.round(dollarsToCents(this.state.minPrice)),
      min_nights: this.state.minNights,
      min_beam: feetToInches(this.state.minBeam),
      max_beam: feetToInches(this.state.maxBeam),
      weekend_days: Object.keys(this.state.weekendDays || {}).filter(
        (key) => this.state.weekendDays[key] === true
      ),
      rates: {
        monday: Math.round(weekdayRate),
        tuesday: Math.round(weekdayRate),
        wednesday: Math.round(weekdayRate),
        thursday: Math.round(
          this.state.weekendDays.thursday ? weekendRate : weekdayRate
        ),
        friday: Math.round(
          this.state.weekendDays.friday ? weekendRate : weekdayRate
        ),
        saturday: Math.round(
          this.state.weekendDays.saturday ? weekendRate : weekdayRate
        ),
        sunday: Math.round(
          this.state.weekendDays.sunday ? weekendRate : weekdayRate
        ),
      },
      space_rate_exceptions_attributes: (this.state.exceptions || []).map(
        (e) => {
          return {
            rate: Math.round(dollarsToCents(e.rate)),
            start_date: moment(e.startDate, "MM-DD-YYYY").toISOString(),
            end_date: moment(e.endDate, "MM-DD-YYYY").toISOString(),
            _destroy: e._destroy || false,
            id: e.id,
          }
        }
      ),
    }
  }

  renderAdvancedOptions() {
    if (this.state.showingAdvancedOptions) {
      return (
        <AdvancedOptions
          minNights={this.state.minNights}
          minPrice={this.state.minPrice}
          minBeam={this.state.minBeam}
          maxBeam={this.state.maxBeam}
          setParentState={(object) => this.setState(object)}
        />
      )
    }
    return null
  }

  renderExceptions() {
    if (this.state.showingExceptions) {
      return (
        <PricingExceptions
          exceptions={this.state.exceptions}
          createException={this.createException}
          removeExceptionByIndex={this.removeExceptionByIndex}
        />
      )
    }
    return null
  }

  renderWarning() {
    if (this.state.id) {
      return (
        <div className="alert alert-warning mt-3">
          Updating an existing rate will create a new rate for today onward.
          <br />
          <button
            type="button"
            className="btn btn-danger mt-3"
            onClick={() => this.props.deleteSpaceRate(this.state.id)}
          >
            Delete Rate?
          </button>
        </div>
      )
    }
    return null
  }

  renderError() {
    if (this.props.errorMessage) {
      return (
        <div className="alert alert-warning mt-3">
          {this.props.errorMessage}
        </div>
      )
    }
    return null
  }

  render() {
    let weekendPricing = null
    if (
      this.state.timeframe === "temporary" ||
      this.state.timeframe === "nightly"
    ) {
      weekendPricing = [
        <div key="weekend" className="rate-form-group col-sm-4 bs-mb-3">
          <h3 className="bs-mt-0 text-right">
            Weekend
            <sup
              className="btn-ghost"
              onClick={() =>
                this.setState({
                  showingWeekendDays: !this.state.showingWeekendDays,
                })
              }
            >
              Edit
            </sup>
          </h3>
          <div className="input-group col-6">
            <span className="input-group-addon">{this.currencySymbol}</span>
            <div className="input-group-addon-mini-left">
              {this.timeframeRateLangauge()}
            </div>
            <input
              type="text"
              className="form-control"
              value={this.state.weekendRate}
              onChange={(e) => this.setState({ weekendRate: e.target.value })}
            />
          </div>
        </div>,
        <WeekendDays
          key="weekday"
          days={this.state.weekendDays}
          toggleDay={this.toggleWeekendDay}
          show={this.state.showingWeekendDays}
        />,
      ]
    }

    return (
      <div className="pricing-form">
        <div className="row nested-row">
          <div className="col-sm-3 ml-sm-5 mr-sm-5">
            <div className="pricing-form-label pull-left">Space Type</div>
            {this.spaceTypeSelect()}
          </div>
          <div className="col-sm-3 mr-sm-5">
            <div className="pricing-form-label pull-left">Timeframe</div>
            {this.timeframeSelect()}
          </div>
          <div className="col-sm-3">
            <div className="pricing-form-label pull-left">Rate Type</div>
            {this.rateTypeSelect()}
          </div>
        </div>

        <hr />

        <div className="row nested-row">
          <div className="col-sm-2">
            <div className="pricing-form-label">Start Date</div>
            <div className="pricing-form-label">End Date</div>
          </div>
          <div className="col-sm-4">
            <div className="form-control-with-icon">
              <i className="fa-kit fa-calendar form-control-icon-left" />
              <DatePicker
                setParentState={(startDate) => this.setState({ startDate })}
                value={this.state.startDate}
                datesDisabled={[]}
                clearBtn={true}
                orientation="top"
              />
            </div>
            <div className="form-control-with-icon" style={{ marginTop: 15 }}>
              <i className="fa-kit fa-calendar form-control-icon-left" />
              <DatePicker
                setParentState={(endDate) => this.setState({ endDate })}
                value={this.state.endDate}
                datesDisabled={[]}
                clearBtn={true}
                startDate={this.state.startDate}
                orientation="top"
              />
            </div>
          </div>
          <div className="col-sm-2">
            <div className="pricing-form-label">Length</div>
          </div>
          <div className="col-sm-4">
            <div className="input-group">
              <div className="input-group-addon-mini">min</div>
              <input
                type="text"
                className="form-control"
                value={this.state.minLength}
                onChange={(e) => this.setState({ minLength: e.target.value })}
              />
              <span className="input-group-addon">ft</span>
            </div>
            <div className="input-group" style={{ marginTop: 15 }}>
              <div className="input-group-addon-mini">max (up to)</div>
              <input
                type="text"
                className="form-control"
                value={this.state.maxLength}
                onChange={(e) => this.setState({ maxLength: e.target.value })}
              />
              <span className="input-group-addon">ft</span>
            </div>
            <div
              className="btn btn-ghost text-right"
              onClick={() => this.toggleAdvancedOptions()}
              style={{ margin: "5px 0 -7px 0" }}
            >
              Advanced Options
            </div>
          </div>
        </div>
        {this.renderAdvancedOptions()}

        <hr />

        <div className="weekday-weekend-wrapper">
          <div className="rate-form-group col-sm-4 bs-mb-3">
            <h3 className="bs-mt-0 text-center">
              {this.state.timeframe === "temporary" ||
              this.state.timeframe === "nightly"
                ? "Weekday"
                : "Rate"}
            </h3>
            <div className="input-group col-6">
              <span className="input-group-addon">{this.currencySymbol}</span>
              <div className="input-group-addon-mini-left">
                {this.timeframeRateLangauge()}
              </div>
              <input
                type="text"
                className="form-control"
                value={this.state.weekdayRate}
                onChange={(e) => this.setState({ weekdayRate: e.target.value })}
              />
            </div>
          </div>
          {weekendPricing}
        </div>
        <div
          className="btn btn-ghost text-center"
          onClick={() => this.toggleExceptions()}
        >
          Add Exceptions
          {this.state.exceptions.length > 0
            ? ` (${this.state.exceptions.length})`
            : ""}
        </div>
        {this.renderExceptions()}
        {this.renderWarning()}
        {this.renderError()}
      </div>
    )
  }
}

SpaceRateForm.propTypes = {
  selectedSpaceRate: PropTypes.object,
  deleteSpaceRate: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
}

export default SpaceRateForm
